import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, Label, Input, Button, Table } from 'reactstrap';
import { Drawer } from '../ui/Drawer';
import { VpSelect } from '../ui/VpSelect';
import {
  useGrantWarehouseRole,
  useRevokeWarehouseRole,
  useSetDefaultWarehouse,
  type UserMembership,
  type WarehouseRole,
} from '../../api/adminMemberships';
import { useTenantContext } from '../../auth/useTenantContext';

export type { UserMembership };

export interface UserWarehouseGrantsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: { id: number; email: string | null };
  /** Current warehouse-role grants for this user. Pass [] if not yet fetched. */
  memberships: UserMembership[];
  /** All warehouses in the tenant (for the add-grant picker). */
  warehouses: Array<{ id: number; code: string; name: string }>;
  inline?: boolean;
}

export function UserWarehouseGrantsDrawer({
  isOpen,
  onClose,
  user,
  memberships,
  warehouses,
  inline,
}: UserWarehouseGrantsDrawerProps) {
  const { t } = useTranslation(['user', 'common']);
  const { token, tenantCode } = useTenantContext();
  const grantM   = useGrantWarehouseRole(token, tenantCode);
  const revokeM  = useRevokeWarehouseRole(token, tenantCode);
  const defaultM = useSetDefaultWarehouse(token, tenantCode);

  const [pickedWarehouseId, setPickedWarehouseId] = useState<number | null>(null);
  const [pickedRole, setPickedRole] = useState<WarehouseRole>('warehouse-admin');
  const [pickedIsDefault, setPickedIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const heldWarehouseIds = new Set(memberships.map((m) => m.warehouse_id));
  const grantable = warehouses.filter((w) => !heldWarehouseIds.has(w.id));

  function submitGrant(): void {
    if (pickedWarehouseId === null) return;
    setError(null);
    grantM.mutate(
      {
        userId: user.id,
        warehouseId: pickedWarehouseId,
        role: pickedRole,
        isDefault: pickedIsDefault,
      },
      {
        onSuccess: () => {
          setPickedWarehouseId(null);
          setPickedIsDefault(false);
        },
        onError: (err: unknown) => {
          const e = err as { body?: { detail?: string }; message?: string };
          setError(e?.body?.detail ?? (e as Error)?.message ?? t('user:warehouseGrants.errorGeneric'));
        },
      },
    );
  }

  const userLabel = user.email ?? t('user:page.fallbackLabel', { id: user.id });

  const inner = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* ── Add grant ──────────────────────────────────────────────────────── */}
        <section>
          <h6 className="fs-13 fw-semibold mb-2">
            {t('user:warehouseGrants.addSection')}
          </h6>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label className="d-flex flex-column gap-1">
              <span className="form-label mb-0" style={{ fontSize: 12 }}>{t('user:warehouseGrants.warehouseLabel')}</span>
              <VpSelect
                ariaLabel={t('user:warehouseGrants.warehouseAria')}
                options={grantable.map((w) => ({ value: w.id, label: `${w.name} (${w.code})` }))}
                value={pickedWarehouseId}
                onChange={(v) => setPickedWarehouseId(v === null ? null : Number(v))}
                placeholder={t('user:warehouseGrants.warehousePlaceholder')}
              />
            </label>

            <label className="d-flex flex-column gap-1">
              <span className="form-label mb-0" style={{ fontSize: 12 }}>{t('user:warehouseGrants.roleLabel')}</span>
              <VpSelect
                ariaLabel={t('user:warehouseGrants.roleAria')}
                options={[
                  { value: 'warehouse-admin', label: t('user:roles.warehouseAdminDesc') },
                  { value: 'voice-supervisor', label: t('user:roles.voiceSupervisorDesc') },
                ]}
                value={pickedRole}
                onChange={(v) => setPickedRole((v ?? 'warehouse-admin') as WarehouseRole)}
              />
            </label>

            <FormGroup check>
              <Input
                id="grant-default"
                type="checkbox"
                checked={pickedIsDefault}
                onChange={(e) => setPickedIsDefault(e.target.checked)}
              />
              <Label check for="grant-default">{t('user:warehouseGrants.defaultCheckbox')}</Label>
            </FormGroup>

            <Button
              type="button"
              color="primary" size="sm"
              disabled={pickedWarehouseId === null || grantM.isPending}
              onClick={submitGrant}
            >
              {grantM.isPending ? t('user:warehouseGrants.adding') : t('user:warehouseGrants.addButton')}
            </Button>
          </div>
        </section>

        {/* ── Current grants ─────────────────────────────────────────────────── */}
        <section>
          <h6 className="fs-13 fw-semibold mb-2">
            {t('user:warehouseGrants.currentSection')}
          </h6>
          {memberships.length === 0 ? (
            <p className="small text-muted mb-0">
              {t('user:warehouseGrants.empty')}
            </p>
          ) : (
            <div className="table-responsive">
            <Table className="table-sm align-middle table-striped mb-0">
              <thead>
                <tr>
                  <th>{t('user:warehouseGrants.tableWarehouse')}</th>
                  <th>{t('user:warehouseGrants.tableRole')}</th>
                  <th>{t('user:warehouseGrants.tableDefault')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {memberships.map((m) => (
                  <tr key={m.id}>
                    <td>
                      {m.warehouse_name} ({m.warehouse_code})
                    </td>
                    <td>{t(`user:roles.${m.role}`, { defaultValue: m.role })}</td>
                    <td>
                      {m.is_default ? (
                        <span aria-label={t('user:warehouseGrants.defaultAria')}>★</span>
                      ) : (
                        <Button
                          type="button"
                          color="light" size="sm"
                          disabled={defaultM.isPending}
                          onClick={() => {
                            setError(null);
                            defaultM.mutate(
                              { userId: user.id, warehouseId: m.warehouse_id, role: m.role },
                              {
                                onError: (err: unknown) => {
                                  const e = err as { body?: { detail?: string }; message?: string };
                                  setError(e?.body?.detail ?? (e as Error)?.message ?? t('user:warehouseGrants.errorGeneric'));
                                },
                              },
                            );
                          }}
                        >
                          {t('user:warehouseGrants.setDefault')}
                        </Button>
                      )}
                    </td>
                    <td>
                      <Button
                        type="button"
                        color="light" size="sm"
                        disabled={revokeM.isPending}
                        onClick={() => {
                          setError(null);
                          revokeM.mutate(
                            { membershipId: m.id },
                            {
                              onError: (err: unknown) => {
                                const e = err as { body?: { detail?: string }; message?: string };
                                setError(e?.body?.detail ?? (e as Error)?.message ?? t('user:warehouseGrants.errorGeneric'));
                              },
                            },
                          );
                        }}
                      >
                        {t('user:warehouseGrants.revoke')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
          )}
        </section>

        {error && (
          <p role="alert" className="text-danger small mb-0">
            {error}
          </p>
        )}

        <div className="d-flex justify-content-end">
          <Button type="button" color="light" size="sm" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
        </div>
      </div>
  );
  return inline ? inner : (
    <Drawer isOpen={isOpen} onClose={onClose} title={t('user:warehouseGrants.drawerTitle', { label: userLabel })}>{inner}</Drawer>
  );
}
